import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { useToast, Toast } from '../../components/ui/Toast';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { resetPassword } = useAuth();
  const toast = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Informe o email cadastrado.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error('Informe um email válido.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      if (err?.code === 'user_not_found') {
        toast.error(err.message);
      } else {
        // Falhas transitórias (rate limit, e-mail) seguem mostrando sucesso,
        // como antes — só "conta inexistente" ganha mensagem própria.
        setSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Left decorative panel ── */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80"
          alt="Wedding"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-rose-950/75" />

        {/* Decorative blobs */}
        <div className="absolute top-1/4 right-8 w-48 h-48 bg-rose-300/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-8 w-36 h-36 bg-pink-300/15 rounded-full blur-2xl" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/brand/icon.png" alt="" className="w-9 h-9 rounded-xl" />
            <span className="font-bold text-xl">Mimo do Casal</span>
          </Link>

          <div>
            <h2 className="text-4xl font-serif font-semibold leading-tight mb-6 text-white/95">
              Não se preocupe, vamos te ajudar a recuperar o acesso.
            </h2>
            <p className="text-white/70 text-lg leading-relaxed">
              Enviaremos um link seguro para o seu email para você redefinir a senha e voltar a gerenciar sua lista de presentes.
            </p>

            <div className="mt-8 p-5 bg-white/10 backdrop-blur border border-white/20 rounded-2xl">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={16} className="text-emerald-300" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Segurança garantida</p>
                  <p className="text-white/60 text-xs mt-0.5">
                    O link expira em 30 minutos e só pode ser usado uma vez.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-gradient-to-br from-rose-50/60 to-white">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <img src="/brand/icon.png" alt="" className="w-9 h-9 rounded-xl" />
              <span className="font-bold text-xl text-gray-900">Mimo do Casal</span>
            </Link>
          </div>

          <AnimatePresence mode="wait">
            {!sent ? (
              /* ── Request form ── */
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Back link */}
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-8 transition-colors group"
                >
                  <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                  Voltar para o login
                </Link>

                {/* Icon */}
                <div className="w-14 h-14 bg-rose-100 border border-rose-200 rounded-2xl flex items-center justify-center mb-6">
                  <Mail size={26} className="text-rose-500" />
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-2">Esqueceu sua senha?</h1>
                <p className="text-gray-500 mb-8">
                  Informe o email cadastrado e enviaremos um link para você redefinir sua senha.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email cadastrado</label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="seu@email.com"
                        autoComplete="email"
                        className="w-full pl-10 pr-4 py-3.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all hover:border-gray-300"
                      />
                    </div>
                  </div>

                  <Button type="submit" size="lg" className="w-full" loading={loading}>
                    {!loading && (
                      <>
                        <span>Enviar link de recuperação</span>
                        <ArrowRight size={18} />
                      </>
                    )}
                    {loading && <span>Enviando...</span>}
                  </Button>
                </form>

                <p className="text-center text-gray-500 text-sm mt-6">
                  Lembrou a senha?{' '}
                  <Link to="/login" className="text-rose-500 font-semibold hover:text-rose-600 transition-colors">
                    Voltar ao login
                  </Link>
                </p>
              </motion.div>
            ) : (
              /* ── Success state ── */
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="text-center"
              >
                {/* Success icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                  className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-green-100 border-2 border-emerald-200 rounded-2xl flex items-center justify-center mx-auto mb-6"
                >
                  <CheckCircle size={36} className="text-emerald-500" />
                </motion.div>

                <h2 className="text-3xl font-bold text-gray-900 mb-3">Email enviado! 🎉</h2>
                <p className="text-gray-500 mb-2 leading-relaxed">
                  Enviamos um link de recuperação para:
                </p>
                <p className="text-rose-600 font-semibold text-lg mb-6 break-all">{email}</p>

                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 mb-8 text-left">
                  <p className="text-sm text-amber-800 font-semibold mb-2">Não recebeu o email?</p>
                  <ul className="text-sm text-amber-700 space-y-1.5">
                    <li>• Verifique a pasta de spam/lixo eletrônico</li>
                    <li>• Aguarde alguns minutos</li>
                    <li>• Confira se o email está correto</li>
                  </ul>
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => { setSent(false); setEmail(''); }}
                    className="w-full py-3.5 border-2 border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Tentar com outro email
                  </button>
                  <Link to="/login">
                    <Button size="lg" className="w-full">
                      <ArrowLeft size={18} />
                      <span>Voltar ao login</span>
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      <Toast toasts={toast.toasts} removeToast={toast.removeToast} />
    </div>
  );
}
